import { Link, useNavigate } from "react-router-dom";
import { Match } from "./MatchCard";
import { MatchScore } from "./MatchScore";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/dateFormatter";

interface MatchListItemProps {
	match: Match;
	className?: string;
}

export const MatchListItem = ({ match, className }: MatchListItemProps) => {
	const navigate = useNavigate();
	return (
		<div
			className={cn("bg-white rounded-lg shadow p-4 hover:shadow-md transition-all cursor-pointer", className)}
			onClick={() => navigate(`/matches/${match.id}`)}
		>
			<div className="grid grid-cols-12 gap-4 items-center">
				{/* Candidate & Job Info - 4 columns */}
				<div className="col-span-4 space-y-1">
					<span className="text-lg font-semibold block">{match.candidateName}</span>
					<span className="text-digger-blue font-medium block">{match.jobTitle}</span>
					<span className="text-sm text-gray-600 block">{match.companyName}</span>
				</div>

				{/* Status - 2 columns */}
				<div className="col-span-2">
					<span className="px-3 py-1 rounded-full text-sm inline-flex items-center justify-center bg-gray-100">
						{match.status || 'New'}
					</span>
				</div>

				{/* Match Date - 2 columns */}
				<div className="col-span-2 text-sm text-gray-600">
					{formatDate(match.createdAt)}
				</div>

				{/* Description - 2 columns */}
				<div className="col-span-2 text-sm text-gray-600 truncate">
					{match.description}
				</div>

				{/* Score & Actions - 2 columns */}
				<div className="col-span-2 flex items-center justify-end gap-4">
					<MatchScore score={match.score} size="md" showText={true} />
					<Link
						to={`/matches/${match.id}`}
						className="inline-flex items-center justify-center px-4 py-1.5 text-sm font-medium rounded-md bg-digger-blue text-white hover:bg-digger-blue-light transition-colors"
						onClick={e => e.stopPropagation()}
					>
						View
					</Link>
				</div>
			</div>
			
			{match.strengths && match.strengths.length > 0 && (
				<div className="mt-3 border-t pt-3">
					<h4 className="text-sm font-medium mb-1">Key strengths:</h4>
					<div className="flex flex-wrap gap-2">
						{match.strengths.map((strength, index) => (
							<span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded">{strength}</span>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
